/**
 * Pure helpers for Forge freeform-capture classification and routing.
 *
 * Solomon's edge planner carries its own copy of this enum/coercion
 * because Deno and the Vite client do not share modules cleanly. This
 * client-side helper defensively normalizes anything that crosses that
 * boundary before action previews or persistence trust it.
 */
export const FORGE_CAPTURE_CATEGORIES = [
  "task",
  "prayer_request",
  "care_request",
  "facility_request",
  "admin_request",
  "forge_improvement",
  "bug",
  "feedback",
] as const;

export type ForgeCaptureCategory = (typeof FORGE_CAPTURE_CATEGORIES)[number];

export interface ForgeCapturedItem {
  title: string;
  category: ForgeCaptureCategory;
}

export type ForgeCapturedItemRoute =
  | { kind: "task" }
  | {
      kind: "request";
      requestCategory: "care" | "facility" | "admin" | "other";
      careType: "prayer" | "other" | null;
      visibility: "normal" | "ministry_team" | "prayer_list";
      addToPrayerList: boolean;
    }
  | {
      kind: "platform_request";
      platformRequestType: "feature" | "bug" | "feedback";
    };

/** Clamp any value to a known category; unknown / non-string -> 'task' (safe default). */
export function coerceForgeCaptureCategory(value: unknown): ForgeCaptureCategory {
  return (FORGE_CAPTURE_CATEGORIES as readonly string[]).includes(value as string)
    ? (value as ForgeCaptureCategory)
    : "task";
}

/**
 * Normalize one raw captured item into a ForgeCapturedItem, or null when it
 * carries no usable title. Accepts a bare string (legacy/model drift -> task)
 * or an object {title, category}.
 */
export function normalizeForgeCapturedItem(raw: unknown): ForgeCapturedItem | null {
  if (typeof raw === "string") {
    const title = raw.trim();
    return title ? { title, category: "task" } : null;
  }

  if (raw && typeof raw === "object") {
    const title = String((raw as { title?: unknown }).title ?? "").trim();
    if (!title) return null;

    const rawCategory = (raw as { category?: unknown }).category;
    const category = coerceForgeCaptureCategory(rawCategory);
    if (
      typeof rawCategory === "string" &&
      rawCategory.length > 0 &&
      category !== rawCategory
    ) {
      console.warn(
        `[forge-capture] unknown category "${rawCategory}" coerced to "task"`,
      );
    }

    return { title, category };
  }

  return null;
}

/** Map a capture category to the public-schema destination it should create. */
export function mapForgeCaptureCategoryToRoute(
  category: ForgeCaptureCategory,
): ForgeCapturedItemRoute {
  switch (category) {
    case "prayer_request":
      return {
        kind: "request",
        requestCategory: "care",
        careType: "prayer",
        visibility: "prayer_list",
        addToPrayerList: true,
      };
    case "care_request":
      return {
        kind: "request",
        requestCategory: "care",
        careType: "other",
        visibility: "ministry_team",
        addToPrayerList: false,
      };
    case "facility_request":
      return {
        kind: "request",
        requestCategory: "facility",
        careType: null,
        visibility: "normal",
        addToPrayerList: false,
      };
    case "admin_request":
      return {
        kind: "request",
        requestCategory: "admin",
        careType: null,
        visibility: "normal",
        addToPrayerList: false,
      };
    case "forge_improvement":
      return { kind: "platform_request", platformRequestType: "feature" };
    case "bug":
      return { kind: "platform_request", platformRequestType: "bug" };
    case "feedback":
      return { kind: "platform_request", platformRequestType: "feedback" };
    case "task":
      return { kind: "task" };
  }
}

/** True when the item should create something other than a plain task. */
export function isForgeCaptureRoutable(category: ForgeCaptureCategory): boolean {
  return mapForgeCaptureCategoryToRoute(category).kind !== "task";
}
